import { useClearReviewCleanup, useReviewCleanup } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { useConfirmAction } from '../shell/useConfirmAction';
import styles from './ReviewCleanupNotice.module.css';

/**
 * Changes that need no review. The request of 7 October 2026, section 5.
 *
 * A case goes to Accounts only when an edit changes the bill: an item, a
 * tooth, the count or the amount. The ones sent before that for any edit at
 * all are still waiting on the Changed list. Management is told how many of
 * those changed nothing of the bill and may clear them, after one question
 * that says the count again. Cleared, a case leaves Changed without joining
 * Reviewed, because nobody reviewed it.
 *
 * Drawn where management reads cases: on Orders, and on Payments for a
 * manager who holds that screen. It asks the server only for a holder of
 * `admin`, and draws nothing while there is nothing to clear, so it is there
 * once after the rule arrives and then gone.
 */
export function ReviewCleanupNotice() {
  const { data: session } = useSession();
  const mayClear = session?.permissions.includes('admin') === true;
  const cleanup = useReviewCleanup(mayClear);
  const clearing = useClearReviewCleanup();
  const ask = useConfirmAction();

  const toClear = cleanup.data?.needNoReview ?? 0;
  if (!mayClear || toClear === 0) return null;

  const clearThem = () =>
    ask(strings.payments.confirmClearChanges(toClear, cleanup.data?.pending ?? 0), () =>
      clearing.mutateAsync().then(
        (cleared) => toast(strings.payments.changesCleared(cleared), { tone: 'success' }),
        (error: Error) => toast(failureWords(error), { tone: 'error' }),
      ),
    );

  return (
    <p className={styles.notice} role="status">
      <span>{strings.payments.changesToClear(toClear)}</span>
      <Button onClick={clearThem} disabled={clearing.isPending}>
        {strings.payments.clearChanges}
      </Button>
    </p>
  );
}
